import { formatCurrency } from 'utils/formatCurrency';
import { ProductTypes } from 'domain/Product';
import { Results } from 'modules/af_tool/api/types';
import { formatPercentage } from 'utils/text';
import { FetchedResources, TFormValues } from '../CalculationId.types';
import { FIELDS } from '../partials/Form/Form.types';
import { CurrentIncome, FutureIncome } from '../utils/incomes';
import { currentPensions, futurePensions } from './pensions';

export type Result = {
  label: string;
  value: string;
  highligthed: boolean;
};

export interface IResultsViewModel {
  loanDetails: Result[];
  amounts: Result[];
}

const createResult = (label: string, value: string, highligthed: boolean = false): Result => ({
  label,
  value,
  highligthed,
});

class ResultsViewModel implements IResultsViewModel {
  private results: Map<string, Result> = new Map([]);

  private mapSectionItems(sectionItems: string[]) {
    return sectionItems.map((item) => this.results.get(item)) as Result[];
  }

  constructor(results: Results) {
    const netCashflow = results.netCashflow;
    this.results.set(
      'startingInterest',
      createResult(
        'Starting interest',
        results.startingInterest ? formatCurrency(results.startingInterest.toString()) : 'N/A',
      ),
    );
    this.results.set(
      'endingInterest',
      createResult(
        'Ending interest',
        results.endingInterest ? formatCurrency(results.endingInterest.toString()) : 'N/A',
      ),
    );
    this.results.set('ltv', createResult('LTV', results.ltv ? formatPercentage(results.ltv.toString()) : 'N/A'));
    this.results.set(
      'incomeBuffer',
      createResult('Income buffer', results.incomeBuffer ? formatPercentage(results.incomeBuffer.toString()) : 'N/A'),
    );

    this.results.set('stage', createResult('Stage', results.stage ? results.stage.toString() : 'N/A'));
    this.results.set(
      'income',
      createResult('Income', results.income ? formatCurrency(results.income.toString()) : 'N/A'),
    );
    this.results.set(
      'buffer',
      createResult('Buffer', results.buffer ? formatCurrency(results.buffer.toString()) : 'N/A'),
    );
    this.results.set(
      'livingCosts',
      createResult('Living costs', results.livingCosts ? formatCurrency(results.livingCosts.toString()) : 'N/A'),
    );
    this.results.set(
      'liabilities',
      createResult('Liabilities', results.liabilities ? formatCurrency(results.liabilities.toString()) : 'N/A'),
    );
    this.results.set(
      'netCashflow',
      createResult(
        'Net cashflow',
        netCashflow ? formatCurrency(netCashflow.toString(), true) : 'N/A',
        netCashflow ? netCashflow > 0 : false,
      ),
    );
    this.results.set(
      'requestedAmount',
      createResult('Requested', results.requestedAmount ? formatCurrency(results.requestedAmount.toString()) : 'N/A'),
    );
    this.results.set(
      'maxPossibleAmount',
      createResult(
        'Max. possible',
        results.maxPossibleAmount ? formatCurrency(results.maxPossibleAmount.toString()) : 'N/A',
      ),
    );
  }

  get loanDetails(): Result[] {
    return this.mapSectionItems(['startingInterest', 'endingInterest', 'ltv', 'incomeBuffer']);
  }

  get amounts(): Result[] {
    return this.mapSectionItems(['requestedAmount', 'maxPossibleAmount']);
  }
}

export const createResultsViewModel = (resultsInitialData: Results): ResultsViewModel => {
  return new ResultsViewModel(resultsInitialData);
};

export const createApplicantFormValues = (): TFormValues['applicant1'] => {
  return {
    [FIELDS.APPLICANT_DATE_OF_BIRTH]: null,
    [FIELDS.APPLICANT_GENDER]: null,
    [FIELDS.APPLICANT_MARITAL_STATUS]: null,
    [FIELDS.APPLICANT_LIFE_STAGE]: null,
    [FIELDS.APPLICANT_RETIREMENT_AGE]: null,
    [FIELDS.CURRENT_INCOME_PREFIX]: CurrentIncome.create(),
    [FIELDS.FUTURE_INCOME_PREFIX]: FutureIncome.create(),
    [FIELDS.LIFE_EVENTS_PREFIX]: {
      [FIELDS.INVESTMENT_PORTFOLIO_VALUE]: 0,
      [FIELDS.VALUE_OF_ASSET_AT_RETIREMENT]: 0,
      [FIELDS.MORTGAGE_PROTECTION_MONTHLY_PAYMENT]: 0,
      [FIELDS.LIFE_INSURANCE_LUMP_SUM]: 0,
      [FIELDS.LIFE_INSURANCE_PREMIUM]: 0,
    },
    [FIELDS.CURRENT_PENSION_PREFIX]: currentPensions.create(),
    [FIELDS.FUTURE_PENSION_PREFIX]: futurePensions.create(),
    [FIELDS.LIABILITY_PREFIX]: [],
  };
};

export const createProductLabel = (product: FetchedResources['products'][0]): string => {
  const status = product.status === 'Decommissioned' ? `(Dec)` : '';

  return `${product.type === ProductTypes.TIO ? 'Term' : ProductTypes.RIO} ${product.code} ${product.name} ${
    product.interestRate
  } ${status}`;
};
